import React, { useContext, useState, useEffect } from "react"
import { ThemeContext } from "styled-components"
import PropTypes from "prop-types"
import { graphql, notGatsby } from "gatsby"

import Reveal from "../utils/Reveal"

import Card from "../atoms/Card"
import FlexGrid from "../atoms/FlexGrid"
import Filter from "../atoms/Filter"
import Section from "../atoms/Section"
import Box from "../atoms/Box"

import Logo from "../molecules/Logo"
import ScrollCarousel from "../molecules/ScrollCarousel"
import FilteredGrid from "../molecules/FilteredGrid"
import SectionTitle from "../molecules/SectionTitle"
import SearchGrid from "../molecules/SearchGrid"
import GenericCarousel from "../molecules/GenericCarousel"
import { sectionTitleHasText } from "../../utils/utils"

const LogoGrid = ({
  cardBack,
  cardFront,
  cardMaxHeight,
  customCardMaxHeight,
  imagePadding,
  overlayImagePadding,
  overlayOpacity,
  imageFit,
  customImageWidth,
  customImageHeight,
  customSizeMobile,
  carouselWidth,
  carouselSpeed,
  cardRevealAlign,
  cardRevealVerticalAlign,
  displayMode = "grid",
  filter,
  LGFilter,
  filters,
  LGFilters,
  flexGrid,
  LGFlexGrid,
  logoItems,
  section,
  sectionTitle,
  LGSectionTitle,
  useCardBack,
  LGCardBack,
  useCardFront,
  LGCardFront,
  zoomOnHover,
  hoverBehavior = "none",
  useSearchFilter,
  searchGrid,
  LGSearchGrid,
  searchedTerm,
  searchProps,
  useCarouselFront,
  carousel,
}) => {
  sectionTitle = LGSectionTitle || sectionTitle
  flexGrid = LGFlexGrid || flexGrid
  cardFront = LGCardFront || cardFront
  cardBack = LGCardBack || cardBack
  filters = LGFilters || filters
  filter = LGFilter || filter

  cardMaxHeight = cardMaxHeight === null ? "fullHeight" : cardMaxHeight
  customCardMaxHeight = customCardMaxHeight === null ? 328 : customCardMaxHeight

  cardFront = {
    ...cardFront,
    useCard: useCardFront,
  }
  cardBack = {
    ...cardFront,
    useCard: hoverBehavior === "info" || hoverBehavior === "all",
    theme: cardBack?.theme,
    bg: cardBack?.bg,
  }

  const isScroll = carousel?.scroll === "automatic"
  const breakpoints = Object.keys(flexGrid?.columns || {})
  const columnsOnBiggestBp =
    flexGrid?.columns?.[breakpoints[breakpoints.length - 1]] ?? 1

  const imagePaddingStyle = {
    _: 4,
    md: !isScroll && columnsOnBiggestBp < 3 ? 7 : 5,
  }

  const logoGridImageStyles = {
    imageStyles: {
      padding: imagePadding === "fill" ? null : imagePaddingStyle,
      objectFit: imagePadding === "fill" ? "cover" : "contain",
    },
    overlayImageStyles: {
      padding: overlayImagePadding === "fill" ? null : imagePaddingStyle,
      objectFit: overlayImagePadding === "fill" ? "cover" : "contain",
      opacity: hoverBehavior === "all" ? overlayOpacity : 1,
    },
  }

  // SEARCH for Logo card info
  const currentTheme = useContext(ThemeContext)
  const [localSearchTerm, setLocalSearchTerm] = useState(searchedTerm ?? "")
  useEffect(() => {
    if (typeof searchedTerm === "string") setLocalSearchTerm(searchedTerm)
  }, [searchedTerm])
  const handleSearch = e => setLocalSearchTerm(e.target.value)

  const resolvedSearchGrid = { ...(LGSearchGrid || searchGrid || {}) }
  // Normalize to lowercase so SearchGrid maps to correct flex alignment
  const alignmentValue = (
    resolvedSearchGrid?.alignSearchBar || "left"
  ).toLowerCase()

  const localSearchProps = {
    ...resolvedSearchGrid,
    useSearch: !!useSearchFilter,
    optionOneBoolField: "searchTitle",
    optionTwoBoolField: "searchSubtitle",
    optionThreeBoolField: "searchDescription",
  }

  // Merge external overrides if provided
  const mergedSearchProps = searchProps
    ? { ...localSearchProps, ...searchProps }
    : localSearchProps

  const plainRichText = rt => {
    if (!rt?.content) return ""
    try {
      const blocks =
        rt?.content?.blocks ||
        (rt?.content?.internal?.content
          ? JSON.parse(rt.content.internal.content)?.blocks
          : [])
      if (!Array.isArray(blocks)) return ""
      return blocks
        .map(b => b?.text || "")
        .join(" ")
        .trim()
    } catch {
      return ""
    }
  }

  const enhancedLogoItems =
    logoItems?.map(item => {
      const st = item?.LSectionTitle || item?.sectionTitle || {}
      return {
        ...item,
        searchTitle: st?.title || "",
        searchSubtitle: st?.label || "",
        searchDescription: plainRichText(st?.descriptionRichText),
      }
    }) || []

  /**
   * carousel controls activated
   */
  if (useCarouselFront) {
    const throughProps = {
      section,
      sectionTitle,
      carousel,
      customImageSize:
        imageFit === "width" ? customImageWidth : customImageHeight,
    }

    /**
     *  instead of using generic carousel scroll, use existing logo grid scroll logic
     *  eventually we should merge the variable logic from logo grid scroll to generic carousel
     */
    return (
      <>
        {isScroll ? (
          <Section {...section}>
            {sectionTitleHasText(sectionTitle, notGatsby) && (
              <Reveal>
                <SectionTitle {...sectionTitle} mb={7} maxWidth={42} />
              </Reveal>
            )}
            <Box
              position="relative"
              width={carousel?.carouselWidth === "normal" ? "100%" : "100vw"}
              ml={carousel?.carouselWidth === "normal" ? undefined : "-50vw"}
              left={carousel?.carouselWidth === "normal" ? undefined : "49.3%"}
            >
              <ScrollCarousel
                cardFront={cardFront}
                cardBack={cardBack}
                align={cardRevealAlign}
                verticalAlign={cardRevealVerticalAlign}
                carouselItems={logoItems}
                hoverBehavior={hoverBehavior}
                zoomOnHover={zoomOnHover}
                logoOrImage="logo"
                customImageWidth={
                  imageFit === "width" ? customImageWidth : null
                }
                customImageHeight={
                  imageFit === "height" ? customImageHeight : null
                }
                customSizeMobile={customSizeMobile}
                carouselSpeed={carousel.carouselSpeed}
                spacing={carousel.spacing}
                logoGridImageStyles={logoGridImageStyles}
              />
            </Box>
          </Section>
        ) : (
          <GenericCarousel {...throughProps}>
            {enhancedLogoItems &&
              enhancedLogoItems.map((logo, i) => (
                <Box
                  width={
                    customImageWidth
                      ? {
                          _: customSizeMobile ? `${customSizeMobile}px` : 8,
                          sm: `${customImageWidth}px`,
                        }
                      : `100%`
                  }
                  height={"auto"}
                >
                  <Logo
                    height="100%"
                    zoomOnHover={zoomOnHover}
                    hoverBehavior={hoverBehavior}
                    cardFront={cardFront}
                    cardBack={cardBack}
                    align={cardRevealAlign}
                    verticalAlign={cardRevealVerticalAlign}
                    cardMaxHeight={cardMaxHeight}
                    {...logoGridImageStyles}
                    {...logo}
                    key={i}
                  />
                </Box>
              ))}
          </GenericCarousel>
        )}
      </>
    )
  }

  /**
   * grid controls activated
   */
  return (
    <Section {...section}>
      {sectionTitleHasText(sectionTitle, notGatsby) && (
        <Reveal>
          <SectionTitle {...sectionTitle} mb={7} maxWidth={42} />
        </Reveal>
      )}
      {useCarouselFront !== true && mergedSearchProps?.useSearch && (
        <SearchGrid
          searchTerm={localSearchTerm}
          handleSearch={handleSearch}
          alignment={alignmentValue}
          key={`logogrid-search-${alignmentValue}`}
          placeholder={resolvedSearchGrid?.defaultSearchBarText || "Search"}
          themeColors={{
            themes: currentTheme?.themes,
            sectionTheme: section?.theme,
          }}
          marginBottom={4}
        />
      )}
      <FilteredGrid
        flexGrid={flexGrid}
        filters={filters}
        filtersStyles={{
          filterLocation: filter?.filterLocation,
          alignMenuItems: filter?.alignMenuItems,
          showFilterLabel: filter?.showFilterLabel,
          layout: filter?.layout,
          filterTextDisplay: filter?.filterTextDisplay,
          dropdownCard: filter?.dropdownCard,
        }} // only sending style fields to avoid possibly overwriting filter data
        searchedTerm={
          typeof searchedTerm === "string" ? searchedTerm : localSearchTerm
        }
        searchProps={mergedSearchProps}
      >
        {enhancedLogoItems &&
          enhancedLogoItems.map((logo, i) => (
            <Logo
              height="100%"
              zoomOnHover={zoomOnHover}
              hoverBehavior={hoverBehavior}
              cardFront={cardFront}
              cardBack={cardBack}
              align={cardRevealAlign}
              verticalAlign={cardRevealVerticalAlign}
              cardMaxHeight={cardMaxHeight}
              customCardMaxHeight={customCardMaxHeight}
              customImageWidth={customImageWidth}
              {...logoGridImageStyles}
              {...logo}
              key={i}
            />
          ))}
      </FilteredGrid>
    </Section>
  )
}

export default LogoGrid

LogoGrid.propTypes = {
  strapi_id: PropTypes.number.isRequired,
  cardBack: PropTypes.shape(Card.strapiProps),
  cardFront: PropTypes.shape(Card.strapiProps),
  cardMaxHeight: PropTypes.oneOf(["default", "square", "custom"]),
  cardRevealAlign: PropTypes.oneOf(["left", "center", "right"]),
  cardRevealVerticalAlign: PropTypes.oneOf(["default", "center"]),
  customCardMaxHeight: PropTypes.number,
  displayMode: PropTypes.oneOf(["grid", "scroll"]),
  filters: PropTypes.arrayOf(PropTypes.shape(Filter.strapiProps)),
  filter: PropTypes.shape(Filter.strapiProps),
  flexGrid: PropTypes.shape(FlexGrid.strapiProps).isRequired,
  logoItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  section: PropTypes.shape(Section.strapiProps).isRequired,
  sectionTitle: PropTypes.shape(SectionTitle.strapiProps).isRequired,
  useCardBack: PropTypes.bool,
  useCardFront: PropTypes.oneOf(["card", "noCard"]),
  useSearchFilter: PropTypes.bool,
  searchGrid: PropTypes.object,
  LGSearchGrid: PropTypes.object,
  searchedTerm: PropTypes.string,
  searchProps: PropTypes.object,
}

export const query = graphql`
  fragment LogoGrid on STRAPI__COMPONENT_SECTIONS_LOGO_GRID {
    strapi_id
    LOGOGRIDSection: section {
      ...Section
    }
    LGSectionTitle: sectionTitle {
      ...SectionTitle
    }
    LGFlexGrid: flexGrid {
      ...FlexGrid
    }
    logoItems {
      ...Logo
    }
    LGCardFront: cardFront {
      ...Card
    }
    LGCardBack: cardBack {
      ...Card
    }
    LGFilters: filters {
      ...Filter
    }
    LGFilter: filter {
      alignMenuItems
      filterLocation
      showFilterLabel
      layout
      filterTextDisplay
      dropdownCard {
        ...Card
      }
    }
    useCardFront
    useCardBack
    cardRevealAlign
    cardRevealVerticalAlign
    displayMode
    zoomOnHover
    hoverBehavior
    cardMaxHeight
    customCardMaxHeight
    imagePadding
    overlayImagePadding
    overlayOpacity
    imageFit
    customImageWidth
    customImageHeight
    customSizeMobile
    carouselWidth
    carouselSpeed
    useSearchFilter
    LGSearchGrid: searchGrid {
      ...SearchGrid
    }
    useCarouselFront
    carousel {
      ...GenericCarousel
    }
  }
`
